import {memo, useEffect, useState} from "react";
import type {CSSProperties, FC, ReactElement, ReactNode} from "react";
import {Image} from "react-native";

import {Alert} from "../ui/alert";
import {Text} from "../ui/text";

interface Dimension {
    width: number;
    height: number;
}

function fitToViewport(imageSize: Dimension, containerSize: Dimension): CSSProperties {
    const {width: imageWidth, height: imageHeight} = imageSize;
    const {width: containerWidth, height: containerHeight} = containerSize;
    const containerRatio = containerWidth / containerHeight;
    const imageRatio = imageWidth / imageHeight;

    if (imageRatio > containerRatio) {
        return {
            width: containerWidth < imageWidth ? "100%" : `${imageWidth}px`,
            height: "auto"
        };
    }
    return {
        width: "auto",
        height: containerHeight < imageHeight ? "100%" : `${imageHeight}px`
    };
}

type ImageRendererProps = {
    url: string;
    imageAlt: string;
    imageSize: Dimension;
    containerRect: Dimension;
    className?: string;
};

function defaultImageRenderer({
    url,
    containerRect,
    imageSize,
    imageAlt
}: ImageRendererProps): ReactElement {
    const style = fitToViewport(imageSize, containerRect);
    return <img alt={imageAlt} className="image-slides-content" src={url} style={style} />;
}

type Props = {
    url: string;
    imageAlt: string;
    containerRect?: Dimension;
    loadingIcon?: ReactElement;
    className?: string;
    imageRenderer?: (param: ImageRendererProps) => ReactNode;
    onLoad?: () => void;
};

const defaultLoadingIcon = <Text variant="xs">Loading...</Text>;
const rect = {width: 0, height: 0};

const ImageRenderer: FC<Props> = ({
    url,
    imageAlt,
    containerRect = rect,
    loadingIcon = defaultLoadingIcon,
    className,
    imageRenderer = defaultImageRenderer,
    onLoad
}) => {
    const [status, setStatus] = useState<"loaded" | "error" | "loading">();
    const [imageSize, setImageSize] = useState<{width: number; height: number}>({
        width: 0,
        height: 0
    });

    useEffect(() => {
        if (url) {
            if (process.env.IS_WEB) {
                const loader = new window.Image();
                const imagePromise = new Promise((resolve, reject) => {
                    loader.onload = resolve;
                    loader.onerror = reject;
                    loader.src = url;
                });

                imagePromise
                    .then(() => {
                        setImageSize({width: loader.naturalWidth, height: loader.naturalHeight});
                        setStatus("loaded");
                        onLoad?.();
                    })
                    .catch(() => {
                        setStatus("error");
                    });
            } else {
                Image.getSize(
                    url,
                    (width, height) => {
                        setImageSize({width, height});
                        setStatus("loaded");
                        onLoad?.();
                    },
                    () => {
                        setStatus("error");
                    }
                );
            }
        }
    }, [onLoad, url]);

    if (status === "error") {
        return (
            <Alert action="error" data-image-url={url} variant="subtle">
                Failed to load the image
            </Alert>
        );
    }

    return status === "loaded"
        ? imageRenderer({url, containerRect, imageSize, imageAlt, className})
        : loadingIcon;
};

export default memo(ImageRenderer);
